/**
 * MoveSideHandle for moving a side of a rectangle by directly manipulating vertices.
 * This bypasses the matrix-based transform system which has limitations with non-uniform scaling.
 *
 * Works with both axis-aligned and rotated rectangles by:
 * 1. Calculating handle positions from actual edge midpoints
 * 2. Moving vertices perpendicular to the edge direction
 *
 * Performance optimization: Uses the ODA view.add() pattern.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D, HandleDrawOptions } from "../types";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { deleteObj } from "../../core/delete";
import { openAs } from "../../core/oda";
import { AbstractHandle } from "./AbstractHandle";
import { createViewGhost } from "../../entities/ghost";
import { MovePolylineVertexCommand } from "../../commands/MovePolylineVertexCommand";
import { MeasurementLabels } from "../../overlay/MeasurementLabels";
import { Facade } from "../../Facade";

/**
 * Edge index (0-3) representing which edge of the rectangle this handle controls.
 * Edge i connects vertex i to vertex (i+1)%4.
 */
export type EdgeIndex = 0 | 1 | 2 | 3;

/**
 * Handle for moving a side of a rectangle by directly manipulating polyline vertices.
 */
export class MoveSideHandle extends AbstractHandle {
  readonly type = HandleType.Resize;
  readonly position: HandlePosition;

  protected entityId: DrawingModule.OdDbObjectId;
  protected edgeIndex: EdgeIndex;

  /** Draw options */
  drawOptions: HandleDrawOptions = {
    fillColor: TRANSFORM_THEME.handleFill,
    strokeColor: TRANSFORM_THEME.handleStroke,
    radius: (HANDLE_SIZES.scale - 1) * HANDLE_SIZES.handleScale,
  };

  /** Original vertex positions for undo/cancel */
  protected originalVertices: { x: number; y: number }[] = [];

  /** Entity handle string */
  protected entityHandle: string = "";

  /** Start world point for delta calculation */
  protected startWorldPoint: Point3D | null = null;

  /** Edge midpoint at drag start — used as the anchor for delta calculation */
  protected startHandleCenter: Point3D | null = null;

  /** Edge normal direction (perpendicular to edge, pointing outward) */
  protected edgeNormal: { x: number; y: number } = { x: 0, y: 0 };

  private measureLabels: MeasurementLabels | null = null;

  /** Ghost entity for view-based rendering (performance optimization) */
  private ghostEntity: DrawingModule.OdDbEntity | null = null;

  /** Original anti-aliasing mode to restore after drag */
  private originalAAMode: number = 0;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityId: DrawingModule.OdDbObjectId,
    edgeIndex: EdgeIndex,
  ) {
    super(viewer, history);
    this.entityId = entityId;
    this.edgeIndex = edgeIndex;

    // Map edge index to a position enum (for compatibility)
    const positionMap: Record<EdgeIndex, HandlePosition> = {
      0: HandlePosition.BottomMiddle,
      1: HandlePosition.LeftMiddle,
      2: HandlePosition.TopMiddle,
      3: HandlePosition.RightMiddle,
    };
    this.position = positionMap[edgeIndex];

    // Calculate world position from actual edge midpoint
    this.worldPosition = this.calculateEdgeMidpoint();

    // Store entity handle string
    this.entityHandle = this.getEntityHandle(entityId);
  }

  /**
   * Calculate the midpoint of the edge from actual vertex positions.
   */
  private calculateEdgeMidpoint(): Point3D {
    const vertices = this.getVertexPositions();
    if (vertices.length < 4) {
      return { x: 0, y: 0, z: 0 };
    }

    const v1 = vertices[this.edgeIndex];
    const v2 = vertices[(this.edgeIndex + 1) % 4];

    return {
      x: (v1.x + v2.x) / 2,
      y: (v1.y + v2.y) / 2,
      z: 0,
    };
  }

  /**
   * Get vertex positions from polyline (read-only, doesn't store).
   * Reads from the ghost entity during a drag so measurements reflect the shadow state.
   */
  private getVertexPositions(): { x: number; y: number }[] {
    if (this.ghostEntity) {
      const ghostPolyline = this.module.OdDbPolyline.cast(this.ghostEntity);
      if (ghostPolyline) {
        const vertices: { x: number; y: number }[] = [];
        const numVerts = ghostPolyline.numVerts();
        for (let i = 0; i < numVerts; i++) {
          const point2d = new this.module.OdGePoint2d(0, 0);
          ghostPolyline.getPointAt(i, point2d);
          vertices.push({ x: point2d.x, y: point2d.y });
          deleteObj(point2d);
        }
        return vertices;
      }
    }

    const vertices: { x: number; y: number }[] = [];
    const polyline = openAs(this.entityId, this.module.OdDbPolyline, 0);
    const numVerts = polyline.numVerts();
    for (let i = 0; i < numVerts; i++) {
      const point2d = new this.module.OdGePoint2d(0, 0);
      polyline.getPointAt(i, point2d);
      vertices.push({ x: point2d.x, y: point2d.y });
      deleteObj(point2d);
    }
    deleteObj(polyline);
    return vertices;
  }

  /**
   * Start side move - store original positions and calculate edge normal.
   */
  start(context: TransformContext): void {
    if (this.isTransforming) return;
    this.isTransforming = true;

    // Store start point for delta calculation
    this.startWorldPoint = { ...context.worldPoint };

    // Store all original vertex positions
    this.originalVertices = this.getVertexPositions();

    // Store handle center (edge midpoint) as the anchor for snap-aware delta
    this.startHandleCenter = this.calculateEdgeMidpoint();

    // Calculate edge normal (perpendicular to edge, pointing outward)
    this.edgeNormal = this.calculateEdgeNormal();

    // Exclude this entity from snap to prevent self-snapping
    if (this.entityHandle) {
      Facade.snap.setExcludedHandles([this.entityHandle]);
    }

    // Create ghost entity for view-based rendering (performance optimization)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    if (view) {
      // PERFORMANCE: Store original AA mode and disable it for faster rendering
      this.originalAAMode = view.antiAliasingMode();
      view.enableAntiAliasing(0);

      // Try interactive mode (may not work in all WebAssembly builds)
      try {
        if (!view.isInInteractivity()) {
          view.beginInteractivity(60);
        }
      } catch {
        // beginInteractivity not supported
      }

      // Ghost only: the original stays in the view underneath, as it does for
      // every other handle. Erasing it here and re-adding it in end()/cancel()
      // is what left the polyline unrendered — view.add() hands ODA a
      // pointer that deleteObj() then destroys on the next line, before any
      // App.Update() has run.
      const ghost = createViewGhost(this.module, this.viewer, this.entityId);
      if (ghost) {
        this.ghostEntity = ghost;
        view.add(ghost, null as unknown as DrawingModule.OdGsModel);
      }
    }
  }

  /**
   * Calculate the outward-pointing normal of the edge.
   */
  private calculateEdgeNormal(): { x: number; y: number } {
    if (this.originalVertices.length < 4) {
      return { x: 0, y: 0 };
    }

    const v1 = this.originalVertices[this.edgeIndex];
    const v2 = this.originalVertices[(this.edgeIndex + 1) % 4];

    // Edge direction
    const edgeX = v2.x - v1.x;
    const edgeY = v2.y - v1.y;

    // Perpendicular (rotate 90 degrees): (-y, x) or (y, -x)
    // We need to determine which direction is "outward"
    let normalX = -edgeY;
    let normalY = edgeX;

    // Normalize
    const len = Math.sqrt(normalX * normalX + normalY * normalY);
    if (len > 0.0001) {
      normalX /= len;
      normalY /= len;
    }

    // Check if normal points outward by testing against center
    const center = this.calculateCenter();
    const edgeMid = {
      x: (v1.x + v2.x) / 2,
      y: (v1.y + v2.y) / 2,
    };

    // Vector from edge midpoint to center
    const toCenterX = center.x - edgeMid.x;
    const toCenterY = center.y - edgeMid.y;

    // If normal points toward center, flip it
    const dot = normalX * toCenterX + normalY * toCenterY;
    if (dot > 0) {
      normalX = -normalX;
      normalY = -normalY;
    }

    return { x: normalX, y: normalY };
  }

  /**
   * Calculate center of the rectangle.
   */
  private calculateCenter(): { x: number; y: number } {
    if (this.originalVertices.length < 4) {
      return { x: 0, y: 0 };
    }

    let sumX = 0;
    let sumY = 0;
    for (const v of this.originalVertices) {
      sumX += v.x;
      sumY += v.y;
    }

    return {
      x: sumX / this.originalVertices.length,
      y: sumY / this.originalVertices.length,
    };
  }

  /**
   * Move side during drag - project movement onto edge normal.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming || !this.startHandleCenter) return;

    // Calculate movement from the handle center so the center tracks the cursor exactly
    const moveX = context.worldPoint.x - this.startHandleCenter.x;
    const moveY = context.worldPoint.y - this.startHandleCenter.y;

    // Project movement onto edge normal to get the perpendicular displacement
    const delta = moveX * this.edgeNormal.x + moveY * this.edgeNormal.y;

    // Get vertex indices for this edge
    const vertexIndices = this.getEdgeVertexIndices();

    // Apply delta along normal direction to the relevant vertices
    if (this.ghostEntity) {
      // Fast path: modify ghost entity
      const ghostPolyline = this.module.OdDbPolyline.cast(this.ghostEntity);
      if (ghostPolyline) {
        for (const idx of vertexIndices) {
          if (idx < this.originalVertices.length) {
            const original = this.originalVertices[idx];
            const newX = original.x + delta * this.edgeNormal.x;
            const newY = original.y + delta * this.edgeNormal.y;
            const point2d = new this.module.OdGePoint2d(newX, newY);
            ghostPolyline.setPointAt(idx, point2d);
            deleteObj(point2d);
          }
        }
        // No recordGraphicsModified needed for ghosts - fast!
      }
    } else {
      // Fallback: modify original (slow path)
      for (const idx of vertexIndices) {
        if (idx < this.originalVertices.length) {
          const original = this.originalVertices[idx];
          const newX = original.x + delta * this.edgeNormal.x;
          const newY = original.y + delta * this.edgeNormal.y;
          this.setVertexPosition(idx, newX, newY);
        }
      }
    }

    // Update view with throttling for better performance
    this.viewer.throttledUpdate();

    this.updateMeasurements();
  }

  private updateMeasurements(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    // Read the current vertex positions after the move
    const verts = this.getVertexPositions();
    if (verts.length < 4) return;

    // Edge 0 (v0→v1) and Edge 2 (v2→v3) are the "width" edges
    // Edge 1 (v1→v2) and Edge 3 (v3→v0) are the "height" edges
    const widthWorld = Math.sqrt(
      (verts[1].x - verts[0].x) ** 2 + (verts[1].y - verts[0].y) ** 2,
    );
    const heightWorld = Math.sqrt(
      (verts[2].x - verts[1].x) ** 2 + (verts[2].y - verts[1].y) ** 2,
    );

    // Convert vertices to screen coords
    // const s0 = this.viewer.worldToScreen(verts[0]);
    const s1 = this.viewer.worldToScreen(verts[1]);
    const s2 = this.viewer.worldToScreen(verts[2]);
    const s3 = this.viewer.worldToScreen(verts[3]);

    // Width guide: along top screen edge (v3→v2, which is edge 2 in world = top edge)
    this.measureLabels.setLine("width", s3.x, s3.y, s2.x, s2.y);
    this.measureLabels.setLabel(
      "width",
      (s3.x + s2.x) / 2,
      Math.min(s3.y, s2.y) - 18,
      MeasurementLabels.formatDistance(widthWorld),
    );

    // Height guide: along right screen edge (v1→v2, which is edge 1 in world = right edge)
    this.measureLabels.setLine("height", s1.x, s1.y, s2.x, s2.y);
    this.measureLabels.setLabel(
      "height",
      Math.max(s1.x, s2.x) + 18,
      (s1.y + s2.y) / 2,
      MeasurementLabels.formatDistance(heightWorld),
    );
  }

  /**
   * End side move - execute commands internally.
   */
  end(context: TransformContext): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    Facade.snap.clearExcludedHandles();

    if (!this.isTransforming || !this.startHandleCenter) return;

    this.isTransforming = false;

    // Clean up ghost and restore original to view
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    }

    // Calculate movement from the handle center and project onto normal
    const moveX = context.worldPoint.x - this.startHandleCenter.x;
    const moveY = context.worldPoint.y - this.startHandleCenter.y;
    const delta = moveX * this.edgeNormal.x + moveY * this.edgeNormal.y;

    // Check if any movement occurred
    if (Math.abs(delta) < 0.001) {
      this.cleanup();
      try {
        this.safeViewerUpdate("MoveSideHandle.end/no-move", {
          entityHandle: this.entityHandle,
        });
      } finally {
        if (ghostToDelete) deleteObj(ghostToDelete);
      }
      return;
    }

    // Get vertex indices for this edge
    const vertexIndices = this.getEdgeVertexIndices();

    // Create a single command for all moved vertices (atomic undo)
    const vertices = vertexIndices
      .filter((idx) => idx < this.originalVertices.length)
      .map((idx) => {
        const original = this.originalVertices[idx];
        return {
          vertexIndex: idx,
          oldPoint: { x: original.x, y: original.y },
          newPoint: {
            x: original.x + delta * this.edgeNormal.x,
            y: original.y + delta * this.edgeNormal.y,
          },
        };
      });

    // Create and execute command (this will apply the change to the original)
    const command = new MovePolylineVertexCommand(this.viewer, {
      handle: this.entityHandle,
      vertices,
    });
    this.history.execute(command);

    this.cleanup();
    try {
      this.safeViewerUpdate("MoveSideHandle.end", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  /**
   * Cancel side move - restore original positions.
   */
  cancel(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    Facade.snap.clearExcludedHandles();

    if (!this.isTransforming) return;

    // Clean up ghost and restore original to view (original was never modified)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    } else {
      // Fallback: restore all original vertex positions (only if no ghost was used)
      for (let i = 0; i < this.originalVertices.length; i++) {
        const original = this.originalVertices[i];
        this.setVertexPosition(i, original.x, original.y);
      }
    }

    this.isTransforming = false;
    this.cleanup();

    try {
      this.safeViewerUpdate("MoveSideHandle.cancel", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  // ============== Private Methods ==============

  /**
   * Clean up state after transform ends.
   */
  private cleanup(): void {
    this.originalVertices = [];
    this.startWorldPoint = null;
    this.startHandleCenter = null;
    this.edgeNormal = { x: 0, y: 0 };
  }

  /**
   * Get vertex indices for this edge.
   * Edge i connects vertex i to vertex (i+1)%4.
   */
  private getEdgeVertexIndices(): number[] {
    return [this.edgeIndex, (this.edgeIndex + 1) % 4];
  }

  /**
   * Set vertex position on polyline.
   */
  private setVertexPosition(index: number, x: number, y: number): void {
    const polyline = openAs(this.entityId, this.module.OdDbPolyline, 1);
    const point2d = new this.module.OdGePoint2d(x, y);
    polyline.setPointAt(index, point2d);
    deleteObj(point2d);

    // Mark entity as graphically modified
    polyline.recordGraphicsModified(true);
    deleteObj(polyline);
  }

  /**
   * Get entity handle string.
   */
  getEntityHandle(entityId: DrawingModule.OdDbObjectId): string {
    const handle = entityId.getHandle();
    const handleStr = handle.ascii();
    const result = handleStr.toString();
    deleteObj(handleStr);
    deleteObj(handle);
    return result;
  }
}
